import { useEffect, useMemo } from 'react'

import { CHAIN_IDS, ChainName } from '@src/configs/chainInfo'
import { CACHE_HALF_FULL_URL, CACHE_HALF_FULL_URL_TESTNET } from '@src/configs/env'
import { ResultCode, StepNames } from '@src/enums'
import { isFeatureEnabled } from '@src/utils'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { useConfigStore } from '@src/zustand_stores'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const wsLogger = createContextLogger('WebSocket')

type LiveOrderData = {
    id: string
    taker_address: string
    maker_address: string
    src_chain: string
    dst_chain: string
    src_asset_address: string
    dst_asset_address: string
    bond_asset_address: string
    src_amount: number
    dst_amount: number
    bond_amount: number
    created_at: Date
    expiration: Date
    status: 'queued' | 'filling' | 'filled' | 'completed'
    completed: boolean
    place_tx: string
    match_tx: string | null
    fill_tx: string | null
    eta: number
}

export const useOrderUpdates = (exchangeId: string, orderId: string) => {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const getOrder = useHistoryStore(state => state.getOrder)

    const webSocketUrl = isFeatureEnabled('USE_TESTNETS_ONLY') ? CACHE_HALF_FULL_URL_TESTNET : CACHE_HALF_FULL_URL
    const wssBaseUrl = webSocketUrl?.replace('https', 'wss')
    const wssUrlEndpoint = useMemo(() => {
        return wssBaseUrl && orderId ? `${wssBaseUrl}/v1/orders/status/${orderId}` : null
    }, [wssBaseUrl, orderId])

    useEffect(() => {
        const transactionData = getOrder(exchangeId)

        wsLogger.info('Re-render triggered. Dependencies:', {
            orderId,
            wssUrlEndpoint,
            availableNetworks,
            exchangeId,
            transactionData,
        })

        if (transactionData.currentStep === StepNames.MATCH_TIMED_OUT) return
        if (!orderId || !wssUrlEndpoint) return

        wsLogger.info('wssUrlEndpoint:', { wssUrlEndpoint })

        const ws = new WebSocket(wssUrlEndpoint)

        wsLogger.info('ws:', { ws })

        ws.onmessage = event => {
            try {
                wsLogger.info('event:', { event })
                const data: LiveOrderData = JSON.parse(event.data as string)

                wsLogger.info('Received data:', { data, completed: data.completed })

                let sourceExplorerUrl = ''
                let destinationExplorerUrl = ''

                try {
                    sourceExplorerUrl = getBlockExplorerUrlFromChainId(CHAIN_IDS[data.src_chain as ChainName])
                } catch (e) {
                    wsLogger.error(`Cannot find explorer URL for source chain ${data.src_chain}`)
                }

                try {
                    destinationExplorerUrl = getBlockExplorerUrlFromChainId(CHAIN_IDS[data.dst_chain as ChainName])
                } catch (e) {
                    wsLogger.error(`Cannot find explorer URL for destination chain ${data.dst_chain}`)
                }

                const sourceTxHash = `${sourceExplorerUrl}/${data.place_tx}`

                const destinationTxHash = data.fill_tx ? `${destinationExplorerUrl}/${data.fill_tx}` : undefined

                const updateData = {
                    exchangeId,
                    currentStep: StepNames.MATCH_EXECUTED,
                    result: {
                        sourceTxHash,
                        destinationTxHash,
                        status: data.completed ? ResultCode.success : ResultCode.pending,
                        message: data.completed ? ResultCode.success : ResultCode.pending,
                    },
                }

                if (data.completed || data.fill_tx) {
                    if (transactionData.currentStep !== StepNames.MATCH_TIMED_OUT) {
                        wsLogger.info('Updating history:', updateData)
                        updateHistory(null, updateData)
                    }
                }
            } catch (error) {
                wsLogger.error('Error processing WebSocket message:', { error })
            }
        }

        ws.onerror = error => {
            wsLogger.error('WebSocket error:', { error })
        }

        ws.onclose = () => {
            wsLogger.info('WebSocket connection closed')
        }

        return () => {
            ws.close()
        }
    }, [orderId, wssUrlEndpoint, availableNetworks, exchangeId])
}
